import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	CREATE_WAREHOUSE_PRINT_JOB,
	GET_LATEST_WAREHOUSE_PRINT_JOB,
	REPRINT_WAREHOUSE_PRINT_JOB,
} from '../../../common/graphql/warehousePrintJob';
import createClientOperationId from '../../../common/utils/createClientOperationId';
import { useOfflineMutation, useOfflineQueue } from '../../offline';
import { Alert } from '../../components/other/Alert';

const createOfflineJob = ({ entry, labelType, serviceDocketId }) => ({
	__typename: 'WarehousePrintJob',
	id: `offline-${entry.id}`,
	created: new Date(entry.createdAt).toISOString(),
	labelType,
	serviceDocketId,
	operationId: entry.variables?.operationId,
	isReprint: Boolean(entry.variables?.jobId),
	status: entry.failureCount ? 'FAILED' : 'QUEUED',
	statusMessage: entry.failureCount
		? entry.lastError || 'This phone could not send the queued label.'
		: 'Queued on this phone. It will be sent when the phone reconnects.',
	warehouse: null,
});

const useWarehousePrintJob = ({
	serviceDocketId,
	labelType,
	enabled = true,
	disableOffline = false,
	alertOnError = true,
}) => {
	const [localJob, setLocalJob] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	// Message of the last failed send (shown by callers that pass alertOnError: false).
	const [submitError, setSubmitError] = useState(null);
	const { queue } = useOfflineQueue();
	const scopedQueue = useMemo(() => (disableOffline ? [] : queue), [disableOffline, queue]);
	const queryVariables = useMemo(() => ({ serviceDocketId, labelType }), [labelType, serviceDocketId]);
	const { data, loading, startPolling, stopPolling } = useQuery(GET_LATEST_WAREHOUSE_PRINT_JOB, {
		variables: queryVariables,
		skip: !enabled || !serviceDocketId || !labelType,
		fetchPolicy: disableOffline ? 'no-cache' : 'cache-and-network',
		errorPolicy: 'all',
	});
	const [createJob] = useOfflineMutation(CREATE_WAREHOUSE_PRINT_JOB, {
		configKey: 'warehousePrintJob',
		disableOffline,
	});
	const [reprintJob] = useOfflineMutation(REPRINT_WAREHOUSE_PRINT_JOB, {
		configKey: 'warehousePrintJob',
		disableOffline,
	});
	useEffect(() => {
		setLocalJob(null);
		setSubmitError(null);
	}, [labelType, serviceDocketId]);

	const queriedJob = data?.latestWarehousePrintJob || null;
	const serverJob = queriedJob?.serviceDocketId === serviceDocketId ? queriedJob : null;
	const pendingEntry = useMemo(() => {
		for (let index = scopedQueue.length - 1; index >= 0; index -= 1) {
			const entry = scopedQueue[index];
			if (entry.configKey !== 'warehousePrintJob') {
				continue;
			}
			if (
				(entry.variables?.serviceDocketId || entry.meta?.serviceDocketId) === serviceDocketId &&
				(entry.variables?.labelType || entry.meta?.labelType) === labelType
			) {
				return entry;
			}
			if (entry.variables?.jobId && entry.variables.jobId === serverJob?.id) {
				return entry;
			}
		}
		return null;
	}, [labelType, scopedQueue, serverJob?.id, serviceDocketId]);
	const queuedJob = pendingEntry ? createOfflineJob({ entry: pendingEntry, labelType, serviceDocketId }) : null;
	const scopedLocalJob = localJob?.serviceDocketId === serviceDocketId ? localJob : null;
	const serverJobIsCurrent = serverJob &&
		scopedLocalJob &&
		(
			serverJob.operationId === scopedLocalJob.operationId ||
			new Date(serverJob.created).getTime() >= new Date(scopedLocalJob.created).getTime()
		);
	const job = queuedJob || (serverJobIsCurrent ? serverJob : scopedLocalJob || serverJob);
	useEffect(() => {
		if (enabled && job?.status === 'QUEUED') {
			startPolling(3000);
			return stopPolling;
		}
		stopPolling();
		return undefined;
	}, [enabled, job?.status, startPolling, stopPolling]);

	const requestLabel = useCallback(async () => {
		setIsSubmitting(true);
		setSubmitError(null);
		try {
			const result = await createJob({
				variables: {
					serviceDocketId,
					labelType,
					operationId: createClientOperationId(`warehouse-label-${labelType.toLowerCase()}`),
				},
			});
			const createdJob = result?.data?.createWarehousePrintJob;
			if (createdJob) {
				setLocalJob({ ...createdJob, serviceDocketId: createdJob.serviceDocketId || serviceDocketId });
			}
			return createdJob;
		}
		catch (error) {
			const message = error?.message || 'Could not send this label to the warehouse printer.';
			setSubmitError(message);
			if (alertOnError) {
				Alert.alert('Label not queued', message);
			}
			return null;
		}
		finally {
			setIsSubmitting(false);
		}
	}, [alertOnError, createJob, labelType, serviceDocketId]);

	const requestReprint = useCallback(async (reason) => {
		if (!job?.id || String(job.id).startsWith('offline-')) {
			return null;
		}
		setIsSubmitting(true);
		setSubmitError(null);
		try {
			const result = await reprintJob({
				variables: {
					jobId: job.id,
					operationId: createClientOperationId(`warehouse-label-reprint-${labelType.toLowerCase()}`),
					reason,
				},
				context: {
					warehousePrint: { serviceDocketId, labelType },
				},
			});
			const createdJob = result?.data?.reprintWarehousePrintJob;
			if (createdJob) {
				setLocalJob({ ...createdJob, serviceDocketId: createdJob.serviceDocketId || serviceDocketId });
			}
			return createdJob;
		}
		catch (error) {
			const message = error?.message || 'Could not queue this reprint.';
			setSubmitError(message);
			if (alertOnError) {
				Alert.alert('Reprint not queued', message);
			}
			return null;
		}
		finally {
			setIsSubmitting(false);
		}
	}, [alertOnError, job?.id, labelType, reprintJob, serviceDocketId]);

	return {
		job,
		loading,
		isSubmitting,
		submitError,
		requestLabel,
		requestReprint,
	};
};

export default useWarehousePrintJob;
